import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';

// Headlines
interface IHeadlineProps {
  color?: string;
  fontWeight?: 'regular' | 'medium' | 'semibold' | 'bold';
  marginBottom?: number;
}

export const H1 = styled.h1<IHeadlineProps>`
  margin-bottom: ${toRem(theme.spacing.xsmall)};
  font-family: ${theme.font.family.averta};
  font-weight: ${theme.font.weight.bold};
  font-size: ${toRem(22)};
  line-height: 100%;
  color: ${theme.color.neutral[900]};

  @media (min-width: ${theme.breakpoint.med}px) {
    font-size: ${toRem(27)};
  }

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${toRem(marginBottom)};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'regular' &&
    css`
      font-weight: ${theme.font.weight.regular};
    `}

${({ fontWeight }) =>
    fontWeight === 'medium' &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

${({ fontWeight }) =>
    fontWeight === 'semibold' &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

${({ fontWeight }) =>
    fontWeight === 'bold' &&
    css`
      font-weight: ${theme.font.weight.bold};
    `}

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}
`;

export const H2 = styled.h2<IHeadlineProps>`
  margin-bottom: 0;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(19.25)};
  line-height: 119.2%;
  color: ${theme.color.neutral[900]};

  @media (min-width: ${theme.breakpoint.med}px) {
    font-size: ${toRem(22)};
  }

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${toRem(marginBottom)};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'regular' &&
    css`
      font-weight: ${theme.font.weight.regular};
    `}

${({ fontWeight }) =>
    fontWeight === 'medium' &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

${({ fontWeight }) =>
    fontWeight === 'semibold' &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

${({ fontWeight }) =>
    fontWeight === 'bold' &&
    css`
      font-weight: ${theme.font.weight.bold};
    `}

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}
`;

export const H3 = styled.h3<IHeadlineProps>`
  margin-bottom: 0;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(13.25)};
  line-height: 100%;
  color: ${theme.color.neutral[900]};

  @media (min-width: ${theme.breakpoint.med}px) {
    font-size: ${toRem(16)};
  }

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${toRem(marginBottom)};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'regular' &&
    css`
      font-weight: ${theme.font.weight.regular};
    `}

${({ fontWeight }) =>
    fontWeight === 'medium' &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

${({ fontWeight }) =>
    fontWeight === 'semibold' &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

${({ fontWeight }) =>
    fontWeight === 'bold' &&
    css`
      font-weight: ${theme.font.weight.bold};
    `}

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}
`;

export const H4 = styled.h4<IHeadlineProps>`
  margin-bottom: 0;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.25)};
  line-height: 143%;
  color: ${theme.color.neutral[900]};

  ${({ marginBottom }) =>
    marginBottom &&
    css`
      margin-bottom: ${toRem(marginBottom)};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'regular' &&
    css`
      font-weight: ${theme.font.weight.regular};
    `}

${({ fontWeight }) =>
    fontWeight === 'medium' &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

${({ fontWeight }) =>
    fontWeight === 'semibold' &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

${({ fontWeight }) =>
    fontWeight === 'bold' &&
    css`
      font-weight: ${theme.font.weight.bold};
    `}

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}
`;

// HR
interface IHorizontalRuleProps {
  color?: string;
  width?: number;
}

export const HR = styled.hr<IHorizontalRuleProps>`
  border: 0;
  border-top: ${toRem(2)} solid ${theme.color.neutral[200]};
  border-bottom: ${toRem(2)} solid transparent;
  width: 100%;

  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}

  ${({ width }) =>
    width &&
    css`
      width: ${width}%;
    `}
`;

// Text
interface ITextProps {
  fontWeight?: 'regular' | 'medium' | 'semibold' | 'bold';
  size?: 'default' | 'small' | 'xsmall' | 'xxsmall';
  capitalize?: boolean;
}

export const Text = styled.p<ITextProps>`
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.25)};
  line-height: 130%;
  color: ${theme.color.neutral[900]};

  @media (min-width: ${theme.breakpoint.med}px) {
    font-size: ${toRem(16.5)};
  }

  ${({ capitalize }) =>
    capitalize &&
    css`
      text-transform: capitalize;
    `}

  ${({ size }) =>
    size === 'small' &&
    css`
      font-size: ${toRem(13.25)};
      line-height: 150%;

      @media (min-width: ${theme.breakpoint.med}px) {
        font-size: ${toRem(13.25)};
      }
    `}

  ${({ size }) =>
    size === 'xsmall' &&
    css`
      font-size: ${toRem(12)};
      line-height: 130%;

      @media (min-width: ${theme.breakpoint.med}px) {
        font-size: ${toRem(12)};
      }
    `}

  ${({ size }) =>
    size === 'xxsmall' &&
    css`
      font-size: ${toRem(11)};
      line-height: 130%;

      @media (min-width: ${theme.breakpoint.med}px) {
        font-size: ${toRem(11)};
      }
    `}

  ${({ fontWeight }) =>
    fontWeight === 'regular' &&
    css`
      font-weight: ${theme.font.weight.regular};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'medium' &&
    css`
      font-weight: ${theme.font.weight.medium};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'semibold' &&
    css`
      font-weight: ${theme.font.weight.semiBold};
    `}

  ${({ fontWeight }) =>
    fontWeight === 'bold' &&
    css`
      font-weight: ${theme.font.weight.bold};
    `}

  a {
    text-decoration: underline;
    cursor: pointer;
  }
`;

// Forms and Fields
export const Label = styled.label`
  margin: 0;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(13.25)};
  line-height: 100%;
  em {
    font-style: italic;
  }
  span {
    color: ${theme.color.neutral[300]};
  }
`;

interface IMessageProps {
  hasError?: boolean;
  indent?: boolean;
  key?: string;
}

export const ValidationMessage = styled(Text).attrs({ size: 'xsmall' })<IMessageProps>`
  ${({ hasError }) =>
    hasError &&
    css`
      color: ${theme.color.danger.medium};
      text-align: center;
    `}

  ${({ indent }) =>
    indent &&
    css`
      padding-left: ${toRem(44)};
    `}
`;

export const FormError = styled(Text).attrs({
  size: 'small',
})`
  &:first-letter {
    text-transform: capitalize;
  }
  margin-bottom: ${toRem(33)};
  color: ${theme.color.danger.medium};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
`;

export const ErrorText = styled(Text).attrs({
  size: 'small',
})`
  &:first-letter {
    text-transform: capitalize;
  }
  color: ${theme.color.danger.medium};
`;
